/**
 * Route Report Issue triage categories to HQ AI roster agents by roleTitle.
 */

/** @typedef {'ui'|'data'|'both'|'other'} ReportIssueCategory */

/**
 * @type {Record<ReportIssueCategory, { label: string, roleKeywords: string[], fallbackKeywords: string[] }>}
 */
export const REPORT_ISSUE_CATEGORIES = {
  ui: {
    label: 'UI / layout / interaction',
    roleKeywords: ['frontend', 'ui/ux', 'ui ux', 'designer'],
    fallbackKeywords: ['full stack', 'fullstack'],
  },
  data: {
    label: 'Data loss / wrong data / reporting',
    roleKeywords: ['data scientist', 'data analyst', 'data engineer'],
    fallbackKeywords: ['backend', 'full stack', 'fullstack'],
  },
  both: {
    label: 'UI and data together',
    roleKeywords: ['full stack', 'fullstack'],
    fallbackKeywords: ['frontend', 'backend', 'tech lead'],
  },
  other: {
    label: 'Other / not sure',
    roleKeywords: ['tech lead', 'product manager', 'full stack', 'fullstack'],
    fallbackKeywords: ['frontend', 'backend'],
  },
};

/**
 * @param {object} agent
 * @returns {boolean}
 */
function isAssignableAgent(agent) {
  if (!agent || typeof agent !== 'object') return false;
  if (agent.isCustomerSuccess || agent.rosterRole === 'CSA') return false;
  if (agent.isCodeReviewer) return false;
  const status = typeof agent.status === 'string' ? agent.status.toLowerCase() : '';
  return status === 'active' || status === 'enabled' || status === '';
}

/**
 * @param {object} agent
 * @returns {string}
 */
function agentSearchText(agent) {
  return [
    agent.roleTitle,
    agent.role,
    agent.name,
    agent.agentName,
    agent.department,
  ]
    .filter((v) => typeof v === 'string')
    .join(' ')
    .toLowerCase();
}

/**
 * Pick the best HQ engineering agent for a triage category.
 * @param {Array<object>} agents
 * @param {ReportIssueCategory|string} category
 * @returns {object|null}
 */
export function pickAgentForReportCategory(agents, category) {
  const list = (Array.isArray(agents) ? agents : []).filter(isAssignableAgent);
  if (!list.length) return null;

  const key =
    category && REPORT_ISSUE_CATEGORIES[category] ? category : 'other';
  const config = REPORT_ISSUE_CATEGORIES[key];

  const matchKeywords = (keywords) =>
    list.find((agent) => {
      const hay = agentSearchText(agent);
      return keywords.some((kw) => hay.includes(kw));
    });

  return (
    matchKeywords(config.roleKeywords) ||
    matchKeywords(config.fallbackKeywords) ||
    list[0] ||
    null
  );
}

/**
 * Build a CSS-ish path for a DOM node (best-effort, not a unique selector).
 * @param {Element|null} el
 * @returns {string}
 */
export function describeDomPath(el) {
  if (!el || !(el instanceof Element)) return '';
  const parts = [];
  let node = el;
  let depth = 0;
  while (node && node.nodeType === 1 && depth < 8) {
    const tag = node.tagName.toLowerCase();
    const id = node.id ? `#${node.id}` : '';
    const cls =
      typeof node.className === 'string' && node.className.trim()
        ? `.${node.className.trim().split(/\s+/).slice(0, 2).join('.')}`
        : '';
    parts.unshift(`${tag}${id}${cls}`);
    node = node.parentElement;
    depth += 1;
  }
  return parts.join(' > ');
}

/**
 * Snapshot useful metadata from a clicked element.
 * @param {Element} el
 * @returns {object}
 */
export function captureElementSnapshot(el) {
  if (!el || !(el instanceof Element)) {
    return {
      tagName: '',
      id: '',
      className: '',
      textSnippet: '',
      outerHtml: '',
      domPath: '',
      href: '',
    };
  }

  const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
  const outer = typeof el.outerHTML === 'string' ? el.outerHTML : '';

  return {
    tagName: el.tagName.toLowerCase(),
    id: el.id || '',
    className:
      typeof el.className === 'string' ? el.className.trim().slice(0, 200) : '',
    textSnippet: text.slice(0, 160),
    outerHtml: outer.slice(0, 4000),
    domPath: describeDomPath(el),
    href:
      el instanceof HTMLAnchorElement && el.href ? String(el.href).slice(0, 500) : '',
  };
}
